import { useContext, useEffect, useRef } from 'react';
import { useSnackbar } from 'notistack';
import { UserContext } from '../App';
import useMyPermissions, { MY_PERMISSIONS_POLL_INTERVAL_MS } from './useMyPermissions';

export const ORG_ACCESS_POLL_INTERVAL_MS = MY_PERMISSIONS_POLL_INTERVAL_MS;

const REVOCATION_MESSAGE = 'Your access to this organization was removed.';

function useOrganizationAccess() {
  const { currentOrgId } = useContext(UserContext);
  const { enqueueSnackbar } = useSnackbar();
  const { hasAccess, isInitialLoading, isRefetching, error, refetch, networkStatus } = useMyPermissions();

  const hadAccessRef = useRef(null);
  const trackedOrgIdRef = useRef(null);
  const shouldSkip = !currentOrgId;

  useEffect(() => {
    if (shouldSkip) {
      hadAccessRef.current = null;
      trackedOrgIdRef.current = null;
      return;
    }

    if (isInitialLoading) {
      return;
    }

    // New org: establish a fresh baseline instead of comparing across orgs.
    if (trackedOrgIdRef.current !== currentOrgId) {
      trackedOrgIdRef.current = currentOrgId;
      hadAccessRef.current = hasAccess;
      return;
    }

    if (hadAccessRef.current === true && !hasAccess) {
      enqueueSnackbar(REVOCATION_MESSAGE, { variant: 'warning', autoHideDuration: 8000 });
    }

    hadAccessRef.current = hasAccess;
  }, [shouldSkip, isInitialLoading, hasAccess, currentOrgId, enqueueSnackbar]);

  return {
    hasAccess,
    isInitialLoading,
    isRefetching,
    error,
    refetch,
    networkStatus
  };
}

export default useOrganizationAccess;
